import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-project-staff-allocation',
  templateUrl: './project-staff-allocation.component.html',
  styleUrls: ['./project-staff-allocation.component.css']
})
export class ProjectStaffAllocationComponent implements OnInit, OnChanges {
  @Input() projectId!: number | string;
  @Input() projectName: string | null = null;

  allocations: any[] = [];
  loading = true;
  adding = false;
  staffList: any[] = [];
  availabilityMap: { [staffId: number]: { used_percent: number; available_percent: number } } = {};
  showAddForm = false;
  /** When set, form is in edit mode for this allocation row. */
  editingAllocationId: number | null = null;
  /** Staff id for the row being edited (dropdown disabled). */
  private editStaffId: number | null = null;

  /** Search/filter on the allocations table (matches project-list dashboard). */
  searchKeyword = '';
  callerOrgId: number | null = null;

  form: FormGroup;

  constructor(
    private api: ApiService,
    private toastr: ToastrMsgService,
    private fb: FormBuilder
  ) {
    this.callerOrgId = this.readCallerOrgId();
    this.form = this.fb.group({
      staff_id: [null, Validators.required],
      start_date: ['', Validators.required],
      end_date: ['', Validators.required],
      allocation_type: ['full'],
      allocation_percent: [50],
      remarks: ['']
    });
    this.form.get('allocation_type')?.valueChanges.subscribe(t => {
      const c = this.form.get('allocation_percent');
      if (t === 'full') {
        this.form.patchValue({ allocation_percent: 100 });
        c?.clearValidators();
      } else {
        c?.setValidators([Validators.required, Validators.min(1), Validators.max(99)]);
      }
      c?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.tryLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId']) {
      this.tryLoad();
    }
  }

  private readCallerOrgId(): number | null {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const u = parsed?.user || parsed;
      const id = u?.org_id ?? (u?.type === 2 ? u?.id : null);
      return id != null ? Number(id) : null;
    } catch {
      return null;
    }
  }

  canEditAllocation(a: any): boolean {
    if (this.callerOrgId == null || a?.org_id == null) return false;
    return Number(a.org_id) === Number(this.callerOrgId);
  }

  private tryLoad(): void {
    const id = this.projectId == null ? null : Number(this.projectId);
    if (id == null || isNaN(id)) {
      this.loading = false;
      return;
    }
    this.load();
  }

  /** `yyyy-MM-dd` for `<input type="date">` */
  private toDateInput(value: string | Date | null | undefined): string {
    if (value == null || value === '') return '';
    if (typeof value === 'string') {
      return value.length >= 10 ? value.slice(0, 10) : value;
    }
    try {
      return value.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }

  load(): void {
    const id = this.projectId == null ? null : Number(this.projectId);
    if (id == null || isNaN(id)) return;
    this.loading = true;
    this.api.getProjectStaffAllocation(id).subscribe({
      next: (res: any) => {
        if (res.success) this.allocations = res.data || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Failed to load staff allocations');
      }
    });
  }

  get displayProjectName(): string {
    return (this.projectName || '').trim();
  }

  get formTitle(): string {
    const name = this.displayProjectName;
    if (this.editingAllocationId != null) {
      return name ? `Edit allocation · ${name}` : 'Edit allocation';
    }
    return name ? `Add staff to ${name}` : 'Add staff to project';
  }

  get submitLabel(): string {
    if (this.adding) {
      return this.editingAllocationId != null ? 'Saving...' : 'Adding...';
    }
    return this.editingAllocationId != null ? 'Save' : 'Add';
  }

  openAddForm(): void {
    this.editingAllocationId = null;
    this.editStaffId = null;
    this.form.get('staff_id')?.enable({ emitEvent: false });
    this.showAddForm = true;
    this.form.reset({ allocation_type: 'full', allocation_percent: 100, remarks: '' });
    this.staffList = [];
    this.availabilityMap = {};
  }

  openEdit(a: any): void {
    if (!this.canEditAllocation(a)) return;
    this.editingAllocationId = a.id;
    this.editStaffId = a.staff_id;
    this.showAddForm = true;
    this.form.get('staff_id')?.disable({ emitEvent: false });
    this.form.patchValue({
      staff_id: a.staff_id,
      start_date: this.toDateInput(a.start_date),
      end_date: this.toDateInput(a.end_date),
      allocation_type: a.allocation_type === 'partial' ? 'partial' : 'full',
      allocation_percent: a.allocation_percent ?? 100,
      remarks: a.remarks || ''
    });
    this.refreshAvailability();
  }

  cancelAdd(): void {
    this.showAddForm = false;
    this.editingAllocationId = null;
    this.editStaffId = null;
    this.form.get('staff_id')?.enable({ emitEvent: false });
    this.staffList = [];
    this.availabilityMap = {};
  }

  refreshAvailability(): void {
    const start = this.form.get('start_date')?.value;
    const end = this.form.get('end_date')?.value;
    if (!start || !end) {
      this.staffList = [];
      this.availabilityMap = {};
      return;
    }
    this.api.getStaffAvailability({
      start_date: start,
      end_date: end,
      exclude_allocation_id: this.editingAllocationId ?? undefined
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.staffList = res.data || [];
          this.availabilityMap = {};
          (res.data || []).forEach((s: any) => {
            this.availabilityMap[s.id] = { used_percent: s.used_percent, available_percent: s.available_percent };
          });
          if (this.editStaffId != null && !this.staffList.some((s: any) => Number(s.id) === Number(this.editStaffId))) {
            const row = this.allocations.find((x: any) => x.id === this.editingAllocationId);
            if (row) {
              this.staffList = [
                {
                  id: row.staff_id,
                  full_name: row.full_name,
                  employee_code: row.employee_code,
                  designation: row.designation,
                  used_percent: 0,
                  available_percent: 100
                },
                ...this.staffList
              ];
            }
          }
        }
      },
      error: () => {
        this.staffList = [];
        this.availabilityMap = {};
      }
    });
  }

  onDateChange(): void {
    if (this.form.get('start_date')?.value && this.form.get('end_date')?.value) {
      this.refreshAvailability();
    }
  }

  getAvailabilityLabel(staffId: number): string {
    const a = this.availabilityMap[staffId];
    if (!a) return '';
    return `${a.available_percent}% available`;
  }

  getStaffOptionLabel(s: any): string {
    const parts: string[] = [s.full_name];
    if (s.employee_code || s.designation) {
      parts.push(String(s.employee_code || s.designation));
    }
    const avail = this.getAvailabilityLabel(s.id);
    if (avail) parts.push(avail);
    const projects = s.allocated_projects_label || this.formatAllocatedProjects(s.allocated_projects);
    if (projects) parts.push(`on ${projects}`);
    return parts.join(' — ');
  }

  private formatAllocatedProjects(projects: any[] | null | undefined): string {
    if (!projects?.length) return '';
    return projects
      .map((p) =>
        p.allocation_percent > 0 && p.allocation_percent < 100
          ? `${p.project_name || `Project #${p.project_id}`} (${p.allocation_percent}%)`
          : p.project_name || `Project #${p.project_id}`
      )
      .join(', ');
  }

  getAvailabilityClass(staffId: number): string {
    const a = this.availabilityMap[staffId];
    if (!a) return '';
    if (a.available_percent <= 0) return 'text-red-600';
    if (a.available_percent < 50) return 'text-amber-600';
    return 'text-green-600';
  }

  onSubmit(): void {
    const id = this.projectId == null ? null : Number(this.projectId);
    if (id == null || isNaN(id)) return;

    const val = this.form.getRawValue();
    if (this.editingAllocationId == null && this.form.invalid) return;
    if (this.editingAllocationId != null) {
      if (!val.start_date || !val.end_date) return;
    }

    const startD = new Date(val.start_date);
    const endD = new Date(val.end_date);
    if (isNaN(startD.getTime()) || isNaN(endD.getTime())) {
      this.toastr.error('Invalid dates');
      return;
    }
    if (endD < startD) {
      this.toastr.error('End date must be on or after start date');
      return;
    }

    const allocation_type = val.allocation_type || 'full';
    const allocation_percent = allocation_type === 'full' ? 100 : (val.allocation_percent || 50);
    const staffId = this.editingAllocationId != null ? (this.editStaffId as number) : val.staff_id;

    const avail = this.availabilityMap[staffId];
    if (avail && allocation_percent > avail.available_percent) {
      this.toastr.error(
        `Only ${avail.available_percent}% is available for this staff in the selected period. Reduce allocation or change dates.`
      );
      return;
    }

    this.adding = true;

    if (this.editingAllocationId != null) {
      this.api
        .updateProjectStaffAllocation(id, this.editingAllocationId, {
          start_date: val.start_date,
          end_date: val.end_date,
          allocation_type,
          allocation_percent,
          remarks: val.remarks || undefined
        })
        .subscribe({
          next: (res: any) => {
            this.adding = false;
            if (res.success) {
              this.toastr.success('Allocation updated');
              this.cancelAdd();
              this.load();
            } else {
              this.toastr.error(res.error || 'Failed to update');
            }
          },
          error: (err) => {
            this.adding = false;
            this.toastr.error(err.error?.error || 'Failed to update allocation');
          }
        });
      return;
    }

    this.api
      .addProjectStaffAllocation(id, {
        staff_id: staffId,
        start_date: val.start_date,
        end_date: val.end_date,
        allocation_type,
        allocation_percent,
        remarks: val.remarks || undefined
      })
      .subscribe({
        next: (res: any) => {
          this.adding = false;
          if (res.success) {
            this.toastr.success('Staff added to project');
            this.cancelAdd();
            this.load();
          } else {
            this.toastr.error(res.error || 'Failed to add');
          }
        },
        error: (err) => {
          this.adding = false;
          this.toastr.error(err.error?.error || 'Failed to add staff');
        }
      });
  }

  remove(a: any): void {
    if (!this.canEditAllocation(a)) return;
    const pid = this.projectId == null ? null : Number(this.projectId);
    if (pid == null || isNaN(pid)) return;
    if (!confirm(`Remove ${a.full_name} from this project?`)) return;
    this.api.deleteProjectStaffAllocation(pid, a.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastr.success('Removed');
          this.load();
        } else {
          this.toastr.error(res.error || 'Failed to remove');
        }
      },
      error: (err) => this.toastr.error(err.error?.error || 'Failed to remove')
    });
  }

  allocationLabel(a: any): string {
    if (a.allocation_type === 'full' || a.allocation_percent === 100) return 'Full';
    return `Partial (${a.allocation_percent}%)`;
  }

  /** Filter the allocations list with the dashboard search box. */
  get filteredAllocations(): any[] {
    const q = (this.searchKeyword || '').trim().toLowerCase();
    if (!q) return this.allocations;
    return this.allocations.filter((a) => {
      const fields = [
        a.full_name,
        a.employee_code,
        a.designation,
        a.contact_email,
        a.remarks,
        a.allocating_org_name,
        this.allocationLabel(a),
      ];
      return fields.some((v) => (v ? String(v).toLowerCase().includes(q) : false));
    });
  }

  applyFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    this.searchKeyword = value;
  }

  clearSearch(): void {
    this.searchKeyword = '';
  }

  /** Counts for the header stat tiles. */
  get totalCount(): number {
    return this.allocations.length;
  }
  get fullCount(): number {
    return this.allocations.filter(
      (a) => a.allocation_type === 'full' || a.allocation_percent === 100
    ).length;
  }
  get partialCount(): number {
    return this.allocations.filter(
      (a) => a.allocation_type === 'partial' && a.allocation_percent < 100
    ).length;
  }

  /** Avatar initials from full name (handles single and multi-word names). */
  initials(name: string | null | undefined): string {
    if (!name) return '—';
    const parts = String(name).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '—';
  }
}
